import React, { useState, useMemo } from 'react';
import { verdictLabel } from '../../utils/formatting';
import { ANOMALY_GROUPS as GROUPS, VERDICT_CONFIG, countVerdicts, countGroups, verdictTooltip } from '../../utils/anomalySignals';
import { signalThresholds } from '../../utils/dealershipSettings';
import { useDealershipSettings } from '../../contexts/settingsContext';
import './AnomalyStrip.css';

// ─── Component ───────────────────────────────────────────────────────────────

function AnomalyStrip({ data, activeVerdicts, onVerdictToggle, onGroupToggle, onClearSignals }) {
    const [expandedGroups, setExpandedGroups] = useState(new Set());
    const thresholds = signalThresholds(useDealershipSettings());

    const verdictCounts = useMemo(() => countVerdicts(data), [data]);
    const groupCounts = useMemo(() => countGroups(verdictCounts), [verdictCounts]);

    const anySignals = GROUPS.some(g => groupCounts[g.key] > 0);
    if (!anySignals) return null;

    const toggleGroup = (key) => {
        setExpandedGroups(prev => {
            const next = new Set(prev);
            next.has(key) ? next.delete(key) : next.add(key);
            return next;
        });
    };

    return (
        <div className="anomaly-strip">
            <div className="anomaly-strip-top">
                <span className="anomaly-strip-label">
                    <svg viewBox="0 0 20 20" fill="currentColor" width="13" height="13">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    Anomaly Signals
                </span>

                <div className="anomaly-group-row">
                    {GROUPS.map(g => {
                        const count = groupCounts[g.key];
                        if (!count) return null;
                        const expanded = expandedGroups.has(g.key);
                        const groupActive = g.verdicts.some(v => activeVerdicts.has(v));
                        return (
                            <button
                                key={g.key}
                                className={`anomaly-card ${g.colorClass} ${groupActive ? 'active' : ''}`}
                                onClick={() => {
                                    toggleGroup(g.key);
                                    const groupVerdicts = g.verdicts.filter(v => verdictCounts[v] > 0);
                                    const allActive = groupVerdicts.every(v => activeVerdicts.has(v));
                                    onGroupToggle(groupVerdicts, !allActive);
                                }}
                            >
                                <span className="anomaly-count">{count.toLocaleString('en-US')}</span>
                                <div className="anomaly-text">
                                    <span className="anomaly-label">{g.label}</span>
                                    <span className="anomaly-sublabel">
                                        {g.verdicts.filter(v => verdictCounts[v] > 0).map(verdictLabel).join(' · ')}
                                    </span>
                                </div>
                                <svg
                                    className={`anomaly-chevron ${expanded ? 'open' : ''}`}
                                    viewBox="0 0 20 20" fill="currentColor" width="13" height="13"
                                >
                                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                            </button>
                        );
                    })}

                    {activeVerdicts.size > 0 && (
                        <button type="button" className="anomaly-clear" onClick={onClearSignals}>
                            <svg viewBox="0 0 20 20" fill="currentColor" width="11" height="11" aria-hidden="true">
                                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                            {activeVerdicts.size > 1
                                ? `Clear anomaly signals (${activeVerdicts.size})`
                                : 'Clear anomaly signals'}
                        </button>
                    )}
                </div>
            </div>

            {/* Expanded verdict chips */}
            {GROUPS.map(g => {
                if (!expandedGroups.has(g.key)) return null;
                const visibleVerdicts = g.verdicts.filter(v => verdictCounts[v] > 0);
                if (!visibleVerdicts.length) return null;
                return (
                    <div key={g.key} className={`anomaly-chips-row anomaly-chips-${g.key}`}>
                        {visibleVerdicts.map(verdict => {
                            const cfg = VERDICT_CONFIG[verdict];
                            const isActive = activeVerdicts.has(verdict);
                            return (
                                <div key={verdict} className="anomaly-chip-wrap">
                                    <button
                                        className={`anomaly-chip ${g.colorClass} ${isActive ? 'active' : ''}`}
                                        onClick={() => onVerdictToggle(verdict)}
                                    >
                                        <span className="chip-dot"></span>
                                        <span className="chip-label">{verdictLabel(verdict)}</span>
                                        <span className="chip-count">{verdictCounts[verdict]}</span>
                                    </button>
                                    {cfg && (
                                        <div className="chip-tooltip-wrap">
                                            <svg viewBox="0 0 20 20" fill="currentColor" width="13" height="13" className="chip-info-icon">
                                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                            </svg>
                                            <div className="chip-tooltip">
                                                <strong>{verdictTooltip(verdict, thresholds)[0]}</strong>
                                                {verdictTooltip(verdict, thresholds).slice(1).map((line, i) => (
                                                    <p key={i}>{line}</p>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                );
            })}
        </div>
    );
}

export default AnomalyStrip;
